import { Done } from '@mui/icons-material'
import { Divider, Link, Stack, Tooltip, Typography, IconButton } from '@mui/material'
import { MouseEventHandler, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CommentStringWithMentions } from '@omnea-app/fe-comments/CommentStringWithMentions'
import { useMarkMentionsResolved } from '@omnea-app/fe-comments/useMarkMentionsResolved'
import { useFormatter } from '@omnea-app/fe-i18n/useFormatter'
import { UserAvatar } from '@omnea-app/fe-users-and-groups/avatar/UserAvatar'
import { getCommentAuthorName } from '@omnea-app/fe-users-and-groups/getCommentAuthorName'
import { BaseUserMention } from './helpers'

interface MentionBoxProps {
  id: string
  createdAt: string
  comment: BaseUserMention['comment']
  name: string
  targetUrl: string
  isResolved: boolean
  requestId: string | null
}

function MentionBox({ id, createdAt, comment, name, targetUrl, isResolved, requestId }: MentionBoxProps) {
  const { mutateAsync: markResolved } = useMarkMentionsResolved()
  const { t } = useTranslation('dashboard')
  const { formatDate } = useFormatter()

  const clickHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.preventDefault()
      void markResolved({ mentions: [{ mentionId: id, requestId }], isResolved: !isResolved })
    },
    [markResolved, id, isResolved, requestId]
  )

  const authorName = getCommentAuthorName({ ...comment, includeInactiveTag: true })

  return (
    <Link href={targetUrl} target="_blank" style={{ textDecoration: 'none' }}>
      <Stack
        direction="row"
        sx={{
          width: '100%',
          borderRadius: 2,
          border: 'stroke.default',
          borderColor: 'border.subtle',
          color: 'text.default',
          p: 3,
          bgcolor: 'bg.surface.default',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'bg.interactive.hover' },
          '&:active': { bgcolor: 'bg.interactive.active' },
        }}
        gap={2}
      >
        <Stack sx={{ width: 24 }}>{comment.user && <UserAvatar user={{ ...comment.user }} />}</Stack>
        <Stack flex={1} sx={{ overflow: 'hidden' }} gap={1}>
          <Stack direction="row" gap={1}>
            <Tooltip title={authorName}>
              <Typography color="text.subtle" variant="small-medium" sx={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                {authorName}
              </Typography>
            </Tooltip>
            <Divider orientation="vertical" />
            <Tooltip title={name}>
              <Typography color="text.subtle" variant="small-medium" noWrap sx={{ fontSize: 10 }}>
                {name}
              </Typography>
            </Tooltip>
          </Stack>
          <CommentStringWithMentions
            variant="Sidebar"
            commentText={comment.payload.text}
            mentionedUsers={comment.mentions}
            showMoreInfoTooltip
          />
          <Typography color="text.subtle" variant="small-medium" sx={{ fontSize: 10 }}>
            {formatDate(new Date(createdAt), { format: 'short', withTime: true })}
          </Typography>
        </Stack>
        <Stack sx={{ width: 20 }} justifyContent="flex-start">
          <Tooltip title={t(isResolved ? 'mentions.mark-unresolved' : 'mentions.mark-resolved')}>
            <IconButton sx={{ padding: 0, mt: '-2px' }} color={isResolved ? 'primary' : 'secondary'} onClick={clickHandler}>
              <Done />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Link>
  )
}

export { MentionBox }
